import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import type { TouchpointSourceType } from '@trello/ad-sequencer';
import { isDesktop } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import type { ExperimentVariations } from '@trello/feature-gate-client';
import { useGetExperimentValue } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import { getAvailableSites } from '../getAvailableSites';
import {
  type TrelloCrossFlowExperiment,
  trelloCrossFlowOpen,
} from '../TrelloCrossFlow';
import { isSourceTargetedByFeatureKey } from './isSourceTargetedByFeatureFlag';
import { useEligibleWorkspacesForProvisioning } from './useEligibleWorkspacesForProvisioning';

const oneTimeMessageSource: Partial<Record<TouchpointSourceType, string>> = {
  boardScreen: '1p-ads-non-ppm-single-user-tool-survey',
  memberHomeWorkspaceSidebarSection: '1p-discovery-ad-left-hand-nav-board-home',
  createBoardInlineDialog: '1p-discovery-ad-left-hand-nav-board-create',
  cardBackDatePicker: '1p-discovery-ad-card-date-range-picker',
  boardDirectory: '1p-discovery-ad-powerup-directory',
};

const experimentFeatureKey = 'trello_xf_non_ppm_free_solo_e2e_vs_board';

export type NonPpmFreeSinglePlayerExperimentVariations = ExperimentVariations<
  typeof experimentFeatureKey,
  'cohort'
>;

export interface CrossFlowForFunnelExperimentParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
}

export const useCrossFlowForNonPpmFreeSinglePlayerExperiment = ({
  source,
  workspaceId,
  boardId,
}: CrossFlowForFunnelExperimentParams): TrelloCrossFlowExperiment<NonPpmFreeSinglePlayerExperimentVariations> => {
  const crossFlow = useCrossFlow();

  const [hasValidAaSession] = useHasValidAaSession();

  const [loadingAvailableSites, setLoadingAvailableSites] = useState(false);
  const [siteEligibility, setSiteEligibility] = useState(false);

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  const isSourceOneTimeMessageDismissed = useCallback(
    () => isOneTimeMessageDismissed(oneTimeMessageSource?.[source] || ''),
    [source, isOneTimeMessageDismissed],
  );

  const eligibleWorkspaceOptions = useEligibleWorkspacesForProvisioning({
    workspaceId,
    entitlementRequired: 'free',
  });

  const [fireExposureEvent, setFireExposureEvent] = useState(false);

  const { cohort } = useGetExperimentValue({
    experimentName: experimentFeatureKey,
    parameter: 'cohort',
    fireExposureEvent,
  });

  const isRuntimeEligibilityChecksPassed = useMemo(
    () =>
      crossFlow.isEnabled &&
      siteEligibility &&
      eligibleWorkspaceOptions.length > 0 &&
      !isSourceOneTimeMessageDismissed(),
    [
      crossFlow.isEnabled,
      eligibleWorkspaceOptions.length,
      isSourceOneTimeMessageDismissed,
      siteEligibility,
    ],
  );

  const getIsTargeted = useCallback(() => {
    return cohort !== 'not-enrolled';
  }, [cohort]);

  const getExperimentCohort = useCallback(() => {
    if (isRuntimeEligibilityChecksPassed) {
      setFireExposureEvent(true);
      return cohort;
    }

    return 'not-enrolled';
  }, [cohort, isRuntimeEligibilityChecksPassed]);

  useEffect(() => {
    const loadSiteEligibility = async () => {
      try {
        setLoadingAvailableSites(true);
        const { sites } = await getAvailableSites();
        const hasNoSites =
          sites?.length > 0 &&
          sites?.filter(({ cloudId }) => cloudId !== 'TRELLO').length === 0;
        setSiteEligibility(hasNoSites);
      } finally {
        setLoadingAvailableSites(false);
      }
    };

    if (
      !loadingAvailableSites &&
      crossFlow.isEnabled &&
      !isDesktop() &&
      getIsTargeted() &&
      isSourceTargetedByFeatureKey({
        featureKey: experimentFeatureKey,
        source,
      })
    ) {
      loadSiteEligibility();
    }

    // Intentionally ignoring loadingAvailableSites dependency
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [
    isSourceOneTimeMessageDismissed,
    source,
    workspaceId,
    crossFlow.isEnabled,
    experimentFeatureKey,
    getIsTargeted,
  ]);

  return useMemo(
    () => ({
      isEnrolled: isRuntimeEligibilityChecksPassed,
      experimentName: experimentFeatureKey,
      getIsTargeted,
      getExperimentCohort,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          boardId,
        },
      ),
    }),
    [
      boardId,
      crossFlow,
      eligibleWorkspaceOptions,
      getExperimentCohort,
      getIsTargeted,
      hasValidAaSession,
      isRuntimeEligibilityChecksPassed,
      source,
      workspaceId,
    ],
  );
};
